// ============================================
// INTRO / EDUCATION PAGE
// ============================================

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Target, 
  Clock, 
  Shield, 
  Sparkles, 
  ArrowRight, 
  CheckCircle,
  MessageCircle,
  Award,
  Users,
  RotateCcw,
} from 'lucide-react';
import { Layout } from '../../components/layout';
import { Button, Card, CardContent } from '../../components/ui';
import { ROUTES, SERVICE_FEE, EXPERT_YEARS } from '../../constants';
import { useFormStore } from '../../store';
import styles from './IntroPage.module.css';

const benefits = [
  {
    icon: <Award size={24} />,
    title: `${EXPERT_YEARS}+ Years Experience`,
    description: 'Our experts have been in the computer industry for over two decades.',
  },
  {
    icon: <MessageCircle size={24} />,
    title: 'Personal WhatsApp Guidance',
    description: 'Get recommendations directly via WhatsApp from real experts, not bots.',
  },
  {
    icon: <Target size={24} />,
    title: 'Personalized Analysis',
    description: 'We analyze your specific usage patterns, not generic categories.',
  },
  {
    icon: <Clock size={24} />,
    title: 'Future-Proof Advice',
    description: 'Recommendations consider your goals for the next 3-5 years.',
  },
];

const whatYouGet = [
  'Direct access to industry experts with 20+ years experience',
  'Personalized recommendations sent via WhatsApp',
  '1-3 carefully selected device options for your needs',
  'Clear explanation of why each option fits your usage',
  'Honest trade-offs and limitations disclosed',
  'Budget-conscious alternatives when relevant',
  'Answers to your follow-up questions',
];

const whyPay = [
  {
    icon: <Users size={20} />,
    text: 'You\'re paying for expert human time, not an algorithm',
  },
  {
    icon: <Shield size={20} />,
    text: 'We have no brand partnerships – unbiased advice only',
  },
  {
    icon: <Sparkles size={20} />,
    text: 'Each recommendation is crafted individually for you',
  },
];

export function IntroPage() {
  const navigate = useNavigate();
  const { hasSavedProgress, startNewForm, currentStep } = useFormStore();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  const hasProgress = hasSavedProgress();

  const handleContinue = () => {
    navigate(ROUTES.FORM);
  };

  const handleStartFresh = () => {
    startNewForm();
    navigate(ROUTES.FORM);
  };

  return (
    <Layout maxWidth="md">
      <div className={styles.container}>
        {/* Hero Section */}
        <section className={styles.hero}>
          <span className={styles.badge}>
            🏆 {EXPERT_YEARS}+ Years Industry Expertise
          </span>
          <h1 className={styles.title}>
            Expert Device Recommendations
            <br />
            <span className={styles.highlight}>Delivered to Your WhatsApp</span>
          </h1>
          <p className={styles.subtitle}>
            Confused about which laptop or desktop to buy? Let our industry veterans with 
            {EXPERT_YEARS}+ years of experience guide you to the perfect choice – personally, via WhatsApp.
          </p>
        </section>

        {/* Benefits Grid */}
        <section className={styles.benefitsSection}>
          <div className={styles.benefitsGrid}>
            {benefits.map((benefit, index) => (
              <Card key={index} variant="bordered" padding="md">
                <CardContent>
                  <div className={styles.benefitIcon}>{benefit.icon}</div>
                  <h3 className={styles.benefitTitle}>{benefit.title}</h3>
                  <p className={styles.benefitDescription}>{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* What You Get */}
        <section className={styles.whatYouGet}>
          <Card variant="elevated" padding="lg">
            <CardContent>
              <h2 className={styles.sectionTitle}>What You'll Receive</h2>
              <ul className={styles.checkList}>
                {whatYouGet.map((item, index) => (
                  <li key={index} className={styles.checkItem}>
                    <CheckCircle size={20} className={styles.checkIcon} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </section>

        {/* Why We Charge */}
        <section className={styles.whyPaySection}>
          <h2 className={styles.sectionTitle}>Why ₹{SERVICE_FEE}?</h2>
          <div className={styles.whyPayList}>
            {whyPay.map((item, index) => (
              <div key={index} className={styles.whyPayItem}>
                <span className={styles.whyPayIcon}>{item.icon}</span>
                <span>{item.text}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Pricing & CTA */}
        <section className={styles.ctaSection}>
          <Card variant="elevated" padding="lg" className={styles.ctaCard}>
            <CardContent>
              <div className={styles.pricing}>
                <span className={styles.priceLabel}>Expert Consultation Fee</span>
                <div className={styles.price}>
                  <span className={styles.rupee}>₹</span>
                  <span className={styles.amount}>{SERVICE_FEE}</span>
                </div>
                <span className={styles.priceNote}>
                  One-time payment • Personal WhatsApp recommendation
                </span>
              </div>
              
              {hasProgress ? (
                <>
                  {/* Show resume option if there's saved progress */}
                  <div className={styles.savedProgress}>
                    <span className={styles.savedProgressText}>
                      You have a form in progress (Step {currentStep}/6)
                    </span>
                  </div>
                  <Button 
                    size="lg" 
                    fullWidth 
                    onClick={handleContinue}
                  >
                    Continue Where You Left Off
                    <ArrowRight size={20} />
                  </Button>
                  <Button 
                    variant="ghost" 
                    fullWidth 
                    onClick={handleStartFresh}
                    className={styles.startFreshButton}
                  >
                    <RotateCcw size={18} />
                    Start Fresh Instead
                  </Button>
                </>
              ) : (
                <>
                  <Button 
                    size="lg" 
                    fullWidth 
                    onClick={handleContinue}
                  >
                    Get My Expert Recommendation
                    <ArrowRight size={20} />
                  </Button>
                </>
              )}

              <p className={styles.ctaNote}>
                Takes about 5 minutes • Recommendations within 24 hours
              </p>
            </CardContent>
          </Card>
        </section>
      </div>
    </Layout>
  );
}
